import PropTypes from 'prop-types';
import React from 'react';

import { calculateLastPage } from 'ravenjs/utils/pagination';
import { formatNumberWithDelimiter } from 'ravenjs/utils/number';

import PaginationBarGoTo from './PaginationBarGoTo';
import PaginationBarPageNav from './PaginationBarPageNav';
import { FooterContainer, ResultSummary } from './styles';

const RESULT_LABEL = 'Result';

/**
 * Redesigned (GN-37779) three-zone pagination footer, matching the attached
 * Figma specs:
 *   - Left:   "Result: N" summary of total records.
 *   - Center: first/prev/page-numbers/next/last navigation.
 *   - Right:  "Go to Page" numeric input + "Go" button.
 *
 * Opt-in only: rendered by `PaginationBar` when the Banner Screen redesign
 * feature flag is enabled. Every existing `Pagination` consumer is untouched.
 *
 * @param {Object}   props
 * @param {number}   props.currentPage The active page (1-based)
 * @param {number}   props.totalRows   The total number of records
 * @param {number}   props.rowsPerPage Rows displayed per page
 * @param {Function} props.onChangePage Called with (event, page) on navigation
 * @param {Object}   props.style       Optional container style overrides
 * @return {JSX.Element}
 */
function BannerPaginationFooter({ currentPage, totalRows, rowsPerPage, onChangePage, style }) {
    const numericTotal = Number(totalRows) || 0;
    const lastPage = calculateLastPage(numericTotal, rowsPerPage);

    // `DataView` injects `style: { padding: '20px 0' }` for the legacy layout.
    // Drop that padding so the Quartz footer keeps its own (`12px 16px`) spacing.
    const { padding, ...containerStyle } = style || {};

    return (
        <FooterContainer style={containerStyle}>
            <ResultSummary>{`${RESULT_LABEL}: ${formatNumberWithDelimiter(
                numericTotal
            )}`}</ResultSummary>
            <PaginationBarPageNav
                currentPage={currentPage}
                lastPage={lastPage}
                onPageChange={onChangePage}
            />
            <PaginationBarGoTo lastPage={lastPage} onSubmit={onChangePage} />
        </FooterContainer>
    );
}

BannerPaginationFooter.propTypes = {
    currentPage: PropTypes.number,
    onChangePage: PropTypes.func,
    rowsPerPage: PropTypes.number,
    style: PropTypes.object,
    totalRows: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

BannerPaginationFooter.defaultProps = {
    currentPage: 1,
    onChangePage: () => {},
    rowsPerPage: 10,
    style: null,
    totalRows: 0,
};

export default BannerPaginationFooter;
